const SESSION_ID_KEY = 'edify_analytics_session_id';
const SESSION_START_KEY = 'edify_analytics_session_start';
const PREVIOUS_PAGE_KEY = 'edify_analytics_previous_page';
const DASHBOARD_VIEW_TIME_KEY = 'edify_analytics_dashboard_view_time';
const TAB_START_TIME_KEY = 'edify_analytics_tab_start_time';
const CURRENT_TAB_KEY = 'edify_analytics_current_tab';
const DASHBOARD_TABS_VIEWED_KEY = 'edify_analytics_dashboard_tabs_viewed';
const DASHBOARD_ACTIONS_CLICKED_KEY = 'edify_analytics_dashboard_actions_clicked';

const SESSION_DURATION_MS = 30 * 60 * 1000;

export const getSessionId = (): string => {
  if (typeof window === 'undefined') return '';

  const storedSessionId = sessionStorage.getItem(SESSION_ID_KEY);
  const storedSessionStart = sessionStorage.getItem(SESSION_START_KEY);
  
  const now = Date.now();
  const sessionStart = storedSessionStart ? parseInt(storedSessionStart, 10) : now;
  const sessionAge = now - sessionStart;

  if (storedSessionId && sessionAge < SESSION_DURATION_MS) {
    return storedSessionId;
  }

  const newSessionId = `session_${now}_${Math.random().toString(36).substr(2, 9)}`;
  sessionStorage.setItem(SESSION_ID_KEY, newSessionId);
  sessionStorage.setItem(SESSION_START_KEY, now.toString());
  
  return newSessionId;
};

export const getDeviceCategory = (): string => {
  if (typeof window === 'undefined') return 'desktop';
  
  const width = window.innerWidth;
  
  if (width <= 600) {
    return 'mobile';
  } else if (width <= 1024) {
    return 'tablet';
  }
  
  return 'desktop';
};

export const getPreviousPage = (): string => {
  if (typeof window === 'undefined') return '';
  
  return sessionStorage.getItem(PREVIOUS_PAGE_KEY) || '';
};

export const setPreviousPage = (path: string): void => {
  if (typeof window === 'undefined') return;
  
  sessionStorage.setItem(PREVIOUS_PAGE_KEY, path);
};

export const updatePreviousPage = (): void => {
  if (typeof window === 'undefined') return;
  
  const currentPath = window.location.pathname;
  const currentPrevious = getPreviousPage();
  
  if (currentPath !== currentPrevious) {
    setPreviousPage(currentPath);
  }
};

export const getDashboardSource = (): string => {
  if (typeof window === 'undefined') return 'direct';
  
  const previousPage = getPreviousPage();
  
  if (!previousPage || previousPage === '/') {
    return 'direct';
  }
  
  if (previousPage.includes('/login') || previousPage.includes('/register')) {
    return 'onboarding';
  }
  
  if (previousPage === '/dashboard') {
    return 'navigation';
  }
  
  if (previousPage.includes('/select-type') || previousPage.includes('/register-form') || previousPage.includes('/resumen')) {
    return 'onboarding';
  }
  
  return 'navigation';
};

export const checkFirstVisitToday = (): boolean => {
  if (typeof window === 'undefined') return true;
  
  const today = new Date().toDateString();
  const lastVisitDate = localStorage.getItem('edify_last_dashboard_visit');
  
  if (lastVisitDate !== today) {
    localStorage.setItem('edify_last_dashboard_visit', today);
    return true;
  }
  
  return false;
};

export const setDashboardViewTime = (): void => {
  if (typeof window === 'undefined') return;
  
  sessionStorage.setItem(DASHBOARD_VIEW_TIME_KEY, Date.now().toString());
};

export const getTimeOnDashboard = (): number => {
  if (typeof window === 'undefined') return 0;
  
  const viewTime = sessionStorage.getItem(DASHBOARD_VIEW_TIME_KEY);
  if (!viewTime) return 0;
  
  const elapsed = Math.floor((Date.now() - parseInt(viewTime, 10)) / 1000);
  return elapsed;
};

export const setTabStartTime = (tab: string): void => {
  if (typeof window === 'undefined') return;
  
  sessionStorage.setItem(TAB_START_TIME_KEY, Date.now().toString());
  sessionStorage.setItem(CURRENT_TAB_KEY, tab);
};

export const getTimeOnTab = (tab: string): number => {
  if (typeof window === 'undefined') return 0;
  
  const currentTab = sessionStorage.getItem(CURRENT_TAB_KEY);
  if (currentTab !== tab) return 0;
  
  const startTime = sessionStorage.getItem(TAB_START_TIME_KEY);
  if (!startTime) return 0;
  
  const elapsed = Math.floor((Date.now() - parseInt(startTime, 10)) / 1000);
  return elapsed;
};

export const getCurrentTab = (): string => {
  if (typeof window === 'undefined') return '';
  
  return sessionStorage.getItem(CURRENT_TAB_KEY) || '';
};

export const trackTabViewed = (tab: string): void => {
  if (typeof window === 'undefined') return;
  
  const tabsViewed = JSON.parse(sessionStorage.getItem(DASHBOARD_TABS_VIEWED_KEY) || '[]');
  if (!tabsViewed.includes(tab)) {
    tabsViewed.push(tab);
    sessionStorage.setItem(DASHBOARD_TABS_VIEWED_KEY, JSON.stringify(tabsViewed));
  }
};

export const getTabsViewedCount = (): number => {
  if (typeof window === 'undefined') return 0;
  
  const tabsViewed = JSON.parse(sessionStorage.getItem(DASHBOARD_TABS_VIEWED_KEY) || '[]');
  return tabsViewed.length;
};

export const trackActionClicked = (): void => {
  if (typeof window === 'undefined') return;
  
  const currentCount = parseInt(sessionStorage.getItem(DASHBOARD_ACTIONS_CLICKED_KEY) || '0', 10);
  sessionStorage.setItem(DASHBOARD_ACTIONS_CLICKED_KEY, (currentCount + 1).toString());
};

export const getActionsClickedCount = (): number => {
  if (typeof window === 'undefined') return 0;
  
  return parseInt(sessionStorage.getItem(DASHBOARD_ACTIONS_CLICKED_KEY) || '0', 10);
};

export const clearDashboardTracking = (): void => {
  if (typeof window === 'undefined') return;
  
  sessionStorage.removeItem(DASHBOARD_VIEW_TIME_KEY);
  sessionStorage.removeItem(TAB_START_TIME_KEY);
  sessionStorage.removeItem(CURRENT_TAB_KEY);
  sessionStorage.removeItem(DASHBOARD_TABS_VIEWED_KEY);
  sessionStorage.removeItem(DASHBOARD_ACTIONS_CLICKED_KEY);
};

